import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { t } from '../theme';
import { PATHS } from '../navigation';
import { useStore } from '../store';

/**
 * Pair a phone as a scanner by showing a QR on the panel.
 *
 * The phone is far better at this than a wall-mounted iPad — you hold the pack
 * close, under a camera that is not pointing at the room. The token behind the
 * QR is single-use and short-lived on purpose: a code on a kitchen screen is
 * trivially photographed, so it grants only "put things away", never a session.
 */
export function PhonePairing({ onClose }: { onClose: () => void }) {
  const scanVersion = useStore((s) => s.scanVersion);
  const setPhoneLink = useStore((s) => s.setPhoneLink);
  const navigate = useNavigate();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<{ claimed: boolean; itemCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const created = await api.createScanLink();
        if (cancelled) return;
        tokenRef.current = created.token;

        // Lazily loaded: the encoder is only needed on this one screen.
        const QR = await import('qrcode');
        const url = await QR.toDataURL(`${window.location.origin}/s/${created.token}`, {
          width: 620,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: '#0f1a10', light: '#ffffff' },
        });
        if (!cancelled) setDataUrl(url);
      } catch {
        if (!cancelled) setError('Kopplung konnte nicht erstellt werden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      setStatus(await api.scanLinkState(tokenRef.current));
    } catch {
      /* transient */
    }
  }, []);

  // The server pushes 'scan' on claim and on every item, so this is a nudge
  // rather than a poll.
  useEffect(() => {
    void refresh();
  }, [refresh, scanVersion]);

  /**
   * Once the phone is on, the QR code is dead weight and a modal over the
   * fridge is worse than useless — the whole point is watching items land.
   *
   * So pairing hands off: the connection moves into the store, a slim strip
   * takes over the status and the unpair button, and the panel goes to the
   * fridge. The short beat is so it does not simply vanish mid-glance.
   */
  useEffect(() => {
    if (!status?.claimed || !tokenRef.current) return;
    const token = tokenRef.current;
    const handoff = setTimeout(() => {
      setPhoneLink({ token, itemCount: status.itemCount });
      navigate(PATHS.inhalt);
      onClose();
    }, 900);
    return () => clearTimeout(handoff);
  }, [status?.claimed, status?.itemCount, setPhoneLink, navigate, onClose]);

  const unpair = async () => {
    if (tokenRef.current) await api.revokeScanLink(tokenRef.current).catch(() => {});
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          maxWidth: '92vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 30,
          display: 'flex',
          gap: 30,
          alignItems: 'center',
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div
          style={{
            width: 260,
            height: 260,
            flex: 'none',
            borderRadius: 22,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {dataUrl ? (
            <img src={dataUrl} alt="" style={{ width: '100%', height: '100%' }} />
          ) : (
            <span style={{ color: '#666', fontSize: 18 }}>…</span>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
            HANDY ALS SCANNER
          </div>
          <div style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1.15 }}>
            {status?.claimed ? 'Handy verbunden ✓' : 'Mit der Kamera scannen'}
          </div>
          <div style={{ fontSize: 19, color: t.inkMuted, textWrap: 'pretty' }}>
            {error
              ? error
              : status?.claimed
                ? `${status.itemCount} ${status.itemCount === 1 ? 'Artikel' : 'Artikel'} eingeräumt. Das Handy kann nur einräumen — es sieht weder Kühlschrank noch Kalender.`
                : 'Einfach die Kamera-App aufs Bild halten. Der Code gilt 3 Minuten und nur für ein Gerät.'}
          </div>

          {status?.claimed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18 }}>
              <div style={{ width: 10, height: 10, borderRadius: 999, background: t.accent }} />
              aktiv
            </div>
          )}

          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <div
              className="press"
              onClick={onClose}
              style={{ height: 68, padding: '0 28px', borderRadius: 18, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}
            >
              Schließen
            </div>
            <div
              className="press"
              onClick={() => void unpair()}
              style={{ height: 68, padding: '0 28px', borderRadius: 18, border: '1px solid oklch(0.72 0.17 25 / 0.4)', color: 'oklch(0.82 0.14 30)', display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 500 }}
            >
              Trennen
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
